import { isSameDay } from "date-fns";

import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { useTranslation } from "@/translations";

import { formatEventDate, formatEventDateRange } from "./eventDateFormat";

/**
 * Formats an event schedule. Same-day schedules show only the time range unless `includeDate` is set,
 * in which case they show the weekday, date, and time. Multi-day schedules always show the start and end dates.
 */
export function EventSchedule({
  startsAt,
  endsAt,
  includeDate = false,
}: {
  startsAt: Date | string;
  endsAt?: Date | string;
  includeDate?: boolean;
}): React.ReactNode {
  const { i18n } = useTranslation();
  const start = new Date(startsAt);
  const end = endsAt ? new Date(endsAt) : undefined;
  let label: string;

  if (end && !isSameDay(start, end)) {
    label = formatEventDateRange(i18n.language, start, end);
  } else {
    const dateLabel = formatEventDate(i18n.language, start, { includeWeekday: true, includeYear: false });
    const timeLabel = end ? `${formatDate(i18n, "time", start)} – ${formatDate(i18n, "time", end)}` : "";
    label = includeDate ? `${dateLabel}${timeLabel ? ` · ${timeLabel}` : ""}` : timeLabel;
  }

  return <time dateTime={start.toISOString()}>{label}</time>;
}
